import React, { useState } from "react";
import {
  Typography,
  Box,
  Card,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import TimelineIcon from "@mui/icons-material/Timeline";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const takeaways = [
  {
    id: 1,
    text: "95% Confidence in All Financial Goal Achievement",
    icon: <CheckCircleOutlineIcon color="success" />,
    info: "Based on Monte Carlo simulations using historical market data and current portfolio allocation. Considers factors such as: market volatility, inflation rates, contribution rates, and goal timing. The 95% confidence level indicates strong probability of success across various market conditions.",
  },
  {
    id: 2,
    text: "99.7% Confidence in Desired Inflation Adjusted Monthly Income through Life Expectancy",
    icon: <TimelineIcon color="primary" />,
    info: "Calculation incorporates Social Security benefits, pension income, investment returns, and planned withdrawals. Adjusts for inflation using historical CPI data and forward-looking estimates. Life expectancy based on actuarial tables with additional safety margin.",
  },
  {
    id: 3,
    text: "New Business in December 2025 Presents Added Future Uncertainty",
    icon: <WarningAmberIcon color="warning" />,
    info: "Business success rates and cash flow projections based on industry averages and local market conditions. Additional risk factors include: initial capital requirements, market competition, and operational costs. Recommend quarterly review of business plan and financial impact.",
  },
  {
    id: 4,
    text: "Assumptions Include Meeting Target Emergency Fund by October 2025",
    icon: <TrendingUpIcon color="info" />,
    info: "Emergency fund target calculated as 6 months of essential expenses. Monthly contribution schedule accounts for current savings rate and projected income changes. Success criteria includes maintaining fund balance while addressing other financial goals.",
  },
  {
    id: 5,
    text: "Confidence Modeling Based on Risk Adjusted Return over 10,000 Simulated Outcomes",
    icon: <TimelineIcon color="primary" />,
    info: "Sophisticated Monte Carlo analysis using modern portfolio theory principles. Each simulation incorporates: market volatility, correlation between asset classes, rebalancing effects, and tax implications. Results normalized for risk-adjusted comparison across scenarios.",
  },
];

const KeyTakeaways = ({ goals }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTakeaway, setSelectedTakeaway] = useState(null);

  const handleInfoClick = (event, takeaway) => {
    setAnchorEl(event.currentTarget);
    setSelectedTakeaway(takeaway);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setSelectedTakeaway(null);
  };

  const open = Boolean(anchorEl);

  return (
    <Box className="h-full" data-testid="takeaways-card">
      <List
        sx={{
          py: 0,
          "& .MuiListItem-root": {
            py: 0.75,
            px: 0,
          },
          "& .MuiListItemIcon-root": {
            minWidth: 32,
            "& svg": {
              fontSize: "1.2rem",
            },
          },
          "& .MuiTypography-body1": {
            fontSize: "0.875rem",
            lineHeight: 1.4,
          },
          "& .MuiIconButton-root": {
            padding: "2px",
            "& svg": {
              fontSize: "1rem",
            },
          },
          "& .MuiDivider-root": {
            my: 0,
          },
        }}
      >
        {takeaways.map((takeaway, index) => (
          <React.Fragment key={takeaway.id}>
            <ListItem
              sx={{
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.02)",
                },
              }}
            >
              <ListItemIcon>{takeaway.icon}</ListItemIcon>
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    <Typography variant="body1" sx={{ mr: 0.5 }}>
                      {takeaway.text}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={(e) => handleInfoClick(e, takeaway)}
                      sx={{
                        "&:hover": {
                          bgcolor: "rgba(0, 0, 0, 0.04)",
                        },
                      }}
                    >
                      <InfoOutlinedIcon />
                    </IconButton>
                  </Box>
                }
              />
            </ListItem>
            {index < takeaways.length - 1 && <Divider />}
          </React.Fragment>
        ))}
      </List>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={{
          sx: {
            maxWidth: 400,
            p: 1.5,
            boxShadow: 3,
          },
        }}
      >
        {selectedTakeaway && (
          <Box>
            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ fontWeight: "bold" }}
            >
              {selectedTakeaway.text}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.8rem" }}
            >
              {selectedTakeaway.info}
            </Typography>
          </Box>
        )}
      </Popover>
    </Box>
  );
};

export default KeyTakeaways;
